/**
 * BottomSheet — overlay modale che entra dal basso (pattern mobile-native).
 *
 * Riusabile in tutto DietaGest dove serve un'azione contestuale su mobile
 * (es. overlay "Altro" del BottomTabBar, dettaglio paziente da lista,
 * conferme distruttive). Su desktop il chiamante può scegliere se usare
 * `BottomSheet` o un dialog centrato — design.md prescrive bottom-sheet
 * full-width per mobile e centrato per desktop, ma BottomSheet centra
 * comunque un'esperienza coerente quando usato.
 *
 * Implementazione: Radix Dialog con positioning custom + safe-area-inset.
 * Niente nuove dipendenze: solo @radix-ui/react-dialog già in PR2.
 */

import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  /** Mostra X di chiusura in alto a destra (default true). */
  showClose?: boolean;
  /** Max height in vh (default 85). */
  maxHeightVh?: number;
  className?: string;
  children: ReactNode;
}

export function BottomSheet({
  open,
  onOpenChange,
  title,
  description,
  showClose = true,
  maxHeightVh = 85,
  className,
  children,
}: BottomSheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          aria-describedby={description ? 'bottom-sheet-desc' : undefined}
          className={clsx(
            'fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-xl',
            'pb-[env(safe-area-inset-bottom)] focus:outline-none',
            className
          )}
          style={{ maxHeight: `${maxHeightVh}vh` }}
        >
          {/* Drag handle visivo (non funzionale: ergonomia mobile) */}
          <div className="w-12 h-1.5 bg-border-gray rounded-full mx-auto mt-2 mb-2" />

          {(title || showClose) && (
            <div className="flex items-start justify-between gap-3 px-4 pt-2 pb-3">
              <div className="min-w-0">
                {title && (
                  <Dialog.Title className="font-headline-md text-headline-md text-text-main">
                    {title}
                  </Dialog.Title>
                )}
                {description && (
                  <Dialog.Description
                    id="bottom-sheet-desc"
                    className="mt-1 font-body-sm text-text-secondary"
                  >
                    {description}
                  </Dialog.Description>
                )}
              </div>
              {showClose && (
                <Dialog.Close
                  aria-label="Chiudi"
                  className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-text-secondary hover:bg-bg-app focus:outline-none focus-visible:ring-2 focus-visible:ring-magenta-soft"
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: '22px' }}
                    aria-hidden
                  >
                    close
                  </span>
                </Dialog.Close>
              )}
            </div>
          )}

          <div className="overflow-y-auto px-4 pb-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
